import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";

const values = [
  ["Love", "Love is patient and is kind. Love doesn’t envy. Love doesn’t brag, is not proud,", "1 Corinthians 13:4", "WEB"],
  ["Forgiveness", "And be kind to one another, tender hearted, forgiving each other, just as God also in Christ forgave you.", "Ephesians 4:32", "WEB"],
  ["Mercy", "For judgment is without mercy to him who has shown no mercy. Mercy triumphs over judgment.", "James 2:13", "WEB"],
  ["Compassion", "Finally, all of you be like-minded, compassionate, loving as brothers, tenderhearted, courteous,", "1 Peter 3:8", "WEB"],
  ["Kindness", "That which makes a man to be desired is his kindness. A poor man is better than a liar.", "Proverbs 19:22", "WEB"],
  ["Goodness", "for the fruit of the Spirit is in all goodness and righteousness and truth,", "Ephesians 5:9", "WEB"],
  ["Faithfulness", "Never let loving devotion or faithfulness leave you; bind them around your neck, write them on the tablet of your heart.", "Proverbs 3:3", "BSB"],
  ["Patience", "with all lowliness and humility, with patience, bearing with one another in love,", "Ephesians 4:2", "WEB"],
  ["Meekness", "Blessed are the meek, for they will inherit the earth.", "Matthew 5:5", "BSB"],
  ["Humility", "The fear of Yahweh teaches wisdom. Before honor is humility.", "Proverbs 15:33", "WEB"],
  ["Generosity", "you being enriched in everything for all generosity, which produces thanksgiving to God through us.", "2 Corinthians 9:11", "WEB"],
  ["Honesty", "An honest answer given is like a kiss on the lips.", "Proverbs 24:26", "BSB"],
];

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 20px;
  margin-top: 20px;
  @media (max-width: 760px) { grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px; }
`;
const ValueButton = styled.button`
  padding: 28px 12px;
  min-width: 0;
  background: var(--about-surface);
  border: 1px solid var(--about-border);
  border-radius: 4px;
  color: ${p => p.theme.colors.homeGold};
  font: 500 clamp(1.05rem, 2vw, 1.3rem)/1.3 ${p => p.theme.fonts.serif};
  overflow-wrap: anywhere;
  cursor: pointer;
  &:hover, &[aria-expanded="true"] { border-color: ${p => p.theme.colors.accent}; }
  &:focus-visible { outline: 2px solid ${p => p.theme.colors.homeGold}; outline-offset: 4px; }
`;
const Panel = styled.div`
  position: fixed;
  z-index: 1000;
  width: min(320px, calc(100vw - 24px));
  max-height: calc(100dvh - 24px);
  overflow-y: auto;
  padding: 20px;
  border: 1px solid ${p => p.theme.colors.accent};
  border-radius: 6px;
  background: #f3dfa9;
  color: ${p => p.theme.colors.homeNavy};
  box-shadow: 0 12px 32px #0006;
  font-family: ${p => p.theme.fonts.body};
  font-size: 1rem;
  line-height: 1.6;
  blockquote { margin: 0 0 12px; }
  cite { display: block; color: ${p => p.theme.colors.homeNavy}; font-style: normal; font-weight: 600; }
`;

export default function ValueScriptures() {
  const [open, setOpen] = useState(null);
  const [pinned, setPinned] = useState(null);
  const [position, setPosition] = useState(null);
  const buttons = useRef([]);
  const panel = useRef(null);
  const closeTimer = useRef(null);
  const suppressMouseUntil = useRef(0);
  // Touch browsers can synthesize mouse events after a tap. Keep taps click-driven.
  const noteTouch = () => { suppressMouseUntil.current = Date.now() + 1000; };
  const mouseEnter = index => {
    if (Date.now() >= suppressMouseUntil.current) show(index);
  };
  const mouseLeave = () => {
    if (Date.now() >= suppressMouseUntil.current) delayedClose();
  };
  const cancelClose = () => clearTimeout(closeTimer.current);
  const show = index => {
    cancelClose();
    if (pinned !== null) return;
    if (open !== index) setPosition(null);
    setOpen(index);
  };
  const togglePinned = index => {
    cancelClose();
    if (pinned === index) {
      setPinned(null);
      setOpen(null);
    } else {
      if (open !== index) setPosition(null);
      setPinned(index);
      setOpen(index);
    }
  };
  const delayedClose = () => {
    cancelClose();
    if (pinned === null) closeTimer.current = setTimeout(() => setOpen(null), 250);
  };

  useEffect(() => () => clearTimeout(closeTimer.current), []);
  useEffect(() => {
    if (open === null) return;
    const dismiss = event => {
      if (!panel.current?.contains(event.target) && !buttons.current.some(button => button?.contains(event.target))) {
        clearTimeout(closeTimer.current);
        setPinned(null);
        setOpen(null);
      }
    };
    const escape = event => {
      if (event.key === "Escape") {
        clearTimeout(closeTimer.current);
        setPinned(null);
        setOpen(null);
      }
    };
    document.addEventListener("pointerdown", dismiss);
    document.addEventListener("focusin", dismiss);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", dismiss);
      document.removeEventListener("focusin", dismiss);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (open === null) return;
    const place = () => {
      const anchor = buttons.current[open].getBoundingClientRect();
      const popup = panel.current.getBoundingClientRect();
      const inset = 12;
      const left = Math.max(inset, Math.min(anchor.left + (anchor.width - popup.width) / 2, window.innerWidth - popup.width - inset));
      const above = anchor.top - popup.height - 8;
      const preferredTop = above >= inset ? above : anchor.bottom + 8;
      const top = Math.max(inset, Math.min(preferredTop, window.innerHeight - popup.height - inset));
      setPosition({ left, top });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const observer = new ResizeObserver(place);
    observer.observe(panel.current);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open]);

  return <>
    <Grid>
      {values.map(([name], index) => <ValueButton
        key={name}
        type="button"
        ref={element => { buttons.current[index] = element; }}
        aria-expanded={open === index}
        aria-pressed={pinned === index}
        aria-controls={open === index ? "value-scripture-panel" : undefined}
        aria-describedby={open === index ? "value-scripture-panel" : undefined}
        onMouseEnter={() => mouseEnter(index)}
        onMouseLeave={mouseLeave}
        onTouchStart={noteTouch}
        onFocus={() => show(index)}
        onClick={() => togglePinned(index)}
      >{name}</ValueButton>)}
    </Grid>
    {open !== null && createPortal(<Panel
      ref={panel}
      id="value-scripture-panel"
      role="tooltip"
      style={{ left: position?.left ?? 12, top: position?.top ?? 12, visibility: position ? "visible" : "hidden" }}
      onMouseEnter={cancelClose}
      onMouseLeave={mouseLeave}
    >
      <blockquote>“{values[open][1]}”</blockquote>
      <cite>{values[open][2]}</cite>
    </Panel>, document.body)}
  </>;
}
